import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import getApiBase from '../utils/apiBase';
import { beginAnalyticsAuthChange, endAnalyticsAuthChange, trackAnalyticsEvent } from '../utils/analytics';

const AuthContext = createContext();
const API_BASE = getApiBase();

const readError = async (response) => {
  try {
    const data = await response.json();
    return data.error || 'Request failed.';
  } catch (error) {
    return 'Request failed.';
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        credentials: 'include',
      });
      if (!response.ok) {
        setUser(null);
        return null;
      }
      const data = await response.json();
      setUser(data.user || null);
      return data.user || null;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const submitAuth = useCallback(async (path, payload) => {
    await beginAnalyticsAuthChange();
    try {
      const response = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await readError(response));
      }

      const data = await response.json();
      setUser(data.user || null);
      endAnalyticsAuthChange();
      if (data.user) trackAnalyticsEvent(path === '/api/auth/register' ? 'register' : 'login');
      return data.user || null;
    } finally {
      endAnalyticsAuthChange();
    }
  }, []);

  const login = useCallback((payload) => submitAuth('/api/auth/login', payload), [submitAuth]);
  const register = useCallback((payload) => submitAuth('/api/auth/register', payload), [submitAuth]);

  const logout = useCallback(async () => {
    await beginAnalyticsAuthChange();
    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      setUser(null);
      endAnalyticsAuthChange();
    }
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
  }), [user, loading, login, register, logout, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
